import type { AtkinsonParams, AtkinsonParamsC } from '../types';

/**
 * Atkinson error diffusion on a grayscale buffer.
 * 1/8 weight to each of 6 neighbors (right1, right2, bottom-left, bottom, bottom-right, bottom-bottom).
 * 2/8 of error is dropped - gives Atkinson its characteristic high-contrast look.
 * Strength scales the diffused error.
 */
export function atkinsonMask(
  source: ImageData,
  params: AtkinsonParams | AtkinsonParamsC,
): Uint8Array {
  const { density, strength } = params;
  const w = source.width;
  const h = source.height;
  const buffer = new Float32Array(w * h);
  const densityShift = (50 - density) / 50;
  const src = source.data;

  for (let i = 0; i < w * h; i++) {
    buffer[i] = (src[i * 4] ?? 0) / 255 + densityShift;
  }

  const mask = new Uint8Array(w * h);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const oldVal = buffer[i] ?? 0;
      const newVal = oldVal > 0.5 ? 1 : 0;
      mask[i] = newVal;
      const err = ((oldVal - newVal) * strength) / 8;

      if (x + 1 < w) {
        const j = i + 1;
        buffer[j] = buffer[j]! + err;
      }
      if (x + 2 < w) {
        const j = i + 2;
        buffer[j] = buffer[j]! + err;
      }
      if (y + 1 < h) {
        const yi = (y + 1) * w;
        if (x - 1 >= 0) {
          const j = yi + (x - 1);
          buffer[j] = buffer[j]! + err;
        }
        const jB = yi + x;
        buffer[jB] = buffer[jB]! + err;
        if (x + 1 < w) {
          const j = yi + (x + 1);
          buffer[j] = buffer[j]! + err;
        }
      }
      if (y + 2 < h) {
        const j = (y + 2) * w + x;
        buffer[j] = buffer[j]! + err;
      }
    }
  }

  return mask;
}
